import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { Reveal } from "./anim";
import { goToApp } from "@/lib/appUrl";

const TIERS = [
  {
    key: "artist",
    name: "Artist",
    price: "£1.58",
    cadence: "/ month",
    billedNote: "Billed £19 annually",
    desc: "For solo artists shipping releases all year round.",
    features: ["Unlimited releases", "90% royalties", "150+ stores & platforms", "Real-time analytics", "Keep your rights"],
    highlight: false,
    cta: "Get started",
  },
  {
    key: "pro",
    name: "Pro",
    price: "£2.42",
    cadence: "/ month",
    billedNote: "Billed £29 annually",
    desc: "For serious independents who want more reach.",
    features: ["Unlimited releases", "95% royalties", "Music video distribution", "VEVO + Apple Music video", "Advanced analytics", "Priority review"],
    highlight: true,
    cta: "Go Pro",
  },
  {
    key: "label",
    name: "Label",
    price: "£8.25",
    cadence: "/ month",
    billedNote: "Billed £99 annually",
    desc: "For labels releasing under their own name.",
    features: ["Unlimited releases on your label", "100% royalties", "Multiple artists & team seats", "Royalty splits", "Dedicated support"],
    highlight: false,
    cta: "Start label",
  },
];

export default function Pricing() {
  const navigate = useNavigate();
  const pickTier = (tier) => {
    // Persist the selected tier so it survives cross-domain redirect + registration.
    try { window.localStorage.setItem("noizbloc_pending_tier", tier); } catch (e) { /* ignore */ }
    const path = `/register?tier=${encodeURIComponent(tier)}`;
    // On the landing host, hop over to the app subdomain; otherwise use in-app navigate.
    if (!goToApp(path)) navigate(path);
  };
  return (
    <section id="pricing" className="py-24 sm:py-32" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs tracking-[0.25em] uppercase font-bold text-slate-400">Pricing</span>
            <h2 className="mt-4 font-display font-medium tracking-tight text-white text-3xl sm:text-4xl md:text-5xl">
              Fair rates. No hidden cuts.
            </h2>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.07}>
              <div
                data-testid={`pricing-card-${t.name.toLowerCase()}`}
                className={`h-full flex flex-col rounded-3xl p-8 border transition-transform hover:-translate-y-1 duration-300 ${
                  t.highlight
                    ? "bg-white text-black border-white"
                    : "bg-white/[0.02] text-white border-white/10"
                }`}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-2xl tracking-tight">{t.name}</h3>
                  {t.highlight && (
                    <span className="text-[10px] tracking-[0.2em] uppercase font-bold bg-blue-600 text-white px-3 py-1 rounded-full">
                      Popular
                    </span>
                  )}
                </div>
                <p className={`mt-2 text-sm ${t.highlight ? "text-slate-600" : "text-slate-400"}`}>{t.desc}</p>
                <div className="mt-6 flex items-end gap-1">
                  <span className="font-display text-5xl font-medium tracking-tight">{t.price}</span>
                  <span className={`mb-1.5 text-sm ${t.highlight ? "text-slate-500" : "text-slate-400"}`}>{t.cadence}</span>
                </div>
                {t.billedNote && (
                  <div className={`mt-1 text-xs ${t.highlight ? "text-slate-500" : "text-slate-500"}`}>{t.billedNote}</div>
                )}
                <ul className="mt-8 space-y-3 flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check size={18} className={t.highlight ? "text-blue-600 mt-0.5" : "text-blue-400 mt-0.5"} />
                      <span className={t.highlight ? "text-slate-700" : "text-slate-300"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`pricing-cta-${t.name.toLowerCase()}`}
                  onClick={() => pickTier(t.key)}
                  className={`mt-8 inline-flex items-center justify-center rounded-full px-6 py-3.5 font-medium transition-all active:scale-[0.98] ${
                    t.highlight
                      ? "bg-black text-white hover:bg-slate-800"
                      : "bg-white text-black hover:bg-slate-200"
                  }`}
                >
                  {t.cta}
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
